import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasInviteTokenInUrl } from '../utils/inviteTokenHandler';
import { adviserAuthReturn, isAdviserInvitationPath } from '../advisers/invitations';

interface InviteTokenCaptureProps {
  children: React.ReactNode;
}

/**
 * Component that detects invite tokens in URLs and redirects to the invitation page
 * Should be placed high in the component tree, ideally in App.tsx
 */
export default function InviteTokenCapture({ children }: InviteTokenCaptureProps) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Practice invitations are separate from Clerk organisation invitations.
    if (isAdviserInvitationPath(location.pathname) || adviserAuthReturn(location.search)) return;
    // Check if the current URL contains an invite token
    if (hasInviteTokenInUrl() && location.pathname !== '/invitation') {
      navigate('/invitation' + location.search + location.hash, { replace: true });
      return;
    }

    // Special case: If we're on sign-in/sign-up pages, check for Clerk invitation context
    if (
      (location.pathname.includes('/sign-in') || location.pathname.includes('/sign-up')) &&
      location.pathname !== '/invitation'
    ) {
      // Check if this might be part of an invitation flow
      const urlParams = new URLSearchParams(location.search);
      const isInvitationFlow =
        urlParams.get('redirect_url')?.includes('invitation') ||
        urlParams.get('after_sign_in_url')?.includes('invitation') ||
        location.hash.includes('invitation') ||
        // Check if there are any Clerk-specific invitation indicators
        urlParams.has('__clerk_invitation_token') ||
        urlParams.has('invitation') ||
        // Check sessionStorage for a previously captured token
        sessionStorage.getItem('clerk_invite_token');

      if (isInvitationFlow) {
        navigate('/invitation' + location.search + location.hash, { replace: true });
      }
    }
  }, [location, navigate]);

  return <>{children}</>;
}
